import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Save } from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import { SearchBar } from '@/components/SearchBox/SearchBar';
import { createBookingRateQuote } from '@/redux/RateQuote/actions';
import { showToast } from '@/redux/Toast/actions';
import CelebrationButton from '../ui/CelebrationButton/CelebrationButton';
import { useNavigate } from 'react-router-dom';

const CreateBookingModal = ({ open, onClose, onSelectCarrier = () => { }, formdata }) => {

    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const dispatch = useDispatch();

    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [officeType, setOfficeType] = useState();
    const [bookingNumber, setBookingNumber] = useState('');
    const [vessel, setVessel] = useState(0);
    const [carrierId, setCarrierId] = useState(0);
    const { isAnimate } = useSelector((state) => state.toast);

    const navigate = useNavigate();

    useEffect(() => {
        if (!open) {
            setSelectedCarrier(null);
            setOfficeType('');
            setBookingNumber('');
            setVessel('');
        }
    }, [open]);

    if (!open) return null;

    const _saveForm = () => {
        // Validate required fields
        if (!officeType || !bookingNumber || !vessel) {
            dispatch(showToast('Please fill in all required fields.', 'error'));
            return;
        }
        const bookingData = {
            "rateQuoteID": formdata?.rateQuoteSno ?? 0,
            "officE_TYPE_ID": 1,
            "officE_TYPE": officeType,
            "bookinG_NUMBER": bookingNumber,
            "createD_BY": authUser?.firstName + ' ' + authUser?.lastName,
            "vesseL_ID": vessel ?? 0,
            "carriersno": carrierId ?? 0,
        }

        // console.log(bookingData);
        createBookingRateQuote(bookingData).then(result => {

            if ((result?.statuscode === 200 || result?.statuscode === 201)) {

                dispatch(showToast('Booking created successfully!', 'success', true));
                navigate(`/booking`, { state: { bookingId: result?.bookingId } });
                setTimeout(() => {
                    onClose();
                }, 300);
            } else {
                dispatch(showToast(result?.message ?? 'Failed to create booking. Please try again.', 'error'));
            }
        }).catch(error => {
            console.error('Error creating booking:', error);
            dispatch(showToast('An error occurred while creating the booking. Please try again---.', 'error'));
        });
    }

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                    >
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-5 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-white/20 rounded-lg">
                                    <BookOpen className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-xl font-bold text-white">
                                    Create Booking
                                </h2>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-4 bg-white dark:bg-slate-900">
                            {/* Office Type */}
                            <div>
                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Office Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={officeType}
                                    onChange={(e) => setOfficeType(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400"
                                >
                                    <option value="">--Select--</option>
                                    <option value="NEW YORK">NEW YORK</option>
                                </select>
                            </div>

                            {/* Booking Number */}
                            <div>
                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Booking Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={bookingNumber}
                                    onChange={(e) => setBookingNumber(e.target.value)}
                                    placeholder="Enter booking number"
                                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400"
                                />
                            </div>

                            {/* Carrier */}
                            <div>
                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Carrier Name <span className="text-red-500">*</span>
                                </label>
                                <select className='w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400'
                                    value={carrierId}
                                    onChange={(e) => {
                                        setCarrierId(e.target.value);
                                        const selected = formdata?.CarrierDetails.find(carrier => carrier.carrierDetailId === parseInt(e.target.value));
                                        setSelectedCarrier(selected);
                                        setVessel('');

                                        console.log("carrier selected:", selected);
                                    }}
                                >
                                    <option value="">--Select--</option>
                                    {
                                        formdata?.CarrierDetails?.map((carrier, i) => (
                                            <option key={carrier.carrierDetailId} value={carrier.carrierDetailId}>
                                                {carrier.carrierName}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>

                            {/* Vessel */}
                            <div>
                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Vessel <span className="text-red-500">*</span>
                                </label>
                                <select className='w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400'
                                    value={vessel}
                                    onChange={(e) => setVessel(e.target.value)}
                                >
                                    <option value="">--Select--</option>
                                    {
                                        selectedCarrier?.ratequoteVessel?.map((vessel) => (
                                            <option key={vessel.vesselId} value={vessel.vesselId}>
                                                {vessel.vesselName}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <CelebrationButton
                                    onClick={() => _saveForm()}
                                    label={"Save"}
                                    className="w-full px-4 py-2.5 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 hover:from-slate-300 hover:to-slate-400 dark:hover:from-slate-600 dark:hover:to-slate-500 text-slate-900 dark:text-white rounded-lg font-medium transition-all"
                                    icon={<Save className="w-3.5 h-3.5" />}
                                    isSuccess={isAnimate}
                                />

                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CreateBookingModal;